// YouTube URL Parameters - Complete JavaScript Utility

/**
 * Common preset configurations for different use cases
 */
export const YouTubePresets = {
  /**
   * Minimal player - No controls, no branding
   */
  minimal: {
    controls: 0,
    modestbranding: 1,
    rel: 0,
    fs: 0,
    iv_load_policy: 3,
    showinfo: 0,
  },

  /**
   * Autoplay with mute (mobile-friendly)
   */
  autoplayMuted: {
    autoplay: 1,
    mute: 1,
    controls: 1,
  },

  /**
   * Loop single video continuously
   */
  loop: (videoId) => ({
    loop: 1,
    playlist: videoId,
  }),

  /**
   * Privacy-enhanced (no cookies until play)
   * Use with buildYouTubeNoCookieUrl
   */
  privacy: {
    rel: 0,
    modestbranding: 1,
    showinfo: 0,
  },

  /**
   * Background video (no controls, muted, looped)
   */
  background: (videoId) => ({
    autoplay: 1,
    mute: 1,
    controls: 0,
    loop: 1,
    playlist: videoId,
    fs: 0,
    iv_load_policy: 3,
    showinfo: 0,
  }),

  /**
   * React Native / Mobile optimized
   */
  mobile: {
    playsinline: 1,
    rel: 0,
    modestbranding: 1,
    showinfo: 0,
  },
};

/**
 * Parse YouTube URL to extract video ID
 * @param {string} url - YouTube URL (any format)
 * @returns {string} Video ID or empty string if not found
 */
export function parseVideoId(url) {
  if (!url) return "";
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([^&?/]+)/,
    /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return "";
}

/**
 * Build YouTube embed URL with parameters
 * @param {string} videoId - YouTube video ID
 * @param {Object} [params] - Optional player parameters
 * @param {string} [baseUrl] - Base embed URL
 * @returns {string} Complete embed URL
 */
export function buildYouTubeUrl(
  videoId = "",
  params,
  baseUrl = 'https://www.youtube.com/embed/'
) {
  const parsedId = parseVideoId(videoId);
  const ytBaseUrl = `${baseUrl}${parsedId}`;

  if (!params) return ytBaseUrl;

  const queryParams = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.push(`${key}=${value}`);
    }
  });

  return queryParams.length > 0
    ? `${ytBaseUrl}?${queryParams.join('&')}`
    : ytBaseUrl;
}

/**
 * Build YouTube nocookie embed URL (privacy-enhanced mode)
 * @param {string} videoId - YouTube video ID
 * @param {Object} [params] - Optional player parameters
 * @returns {string} Privacy-enhanced embed URL
 */
export function buildYouTubeNoCookieUrl(videoId, params) {
  const url = buildYouTubeUrl(videoId, params);
  return url.replace('youtube.com', 'youtube-nocookie.com');
}

/**
 * Check if value is a valid YouTube video ID
 * @param {string} value 
 * @returns {boolean}
 */
export function isValidVideoId(value) {
  return /^[a-zA-Z0-9_-]{11}$/.test(value);
}

/**
 * Check if value is a valid playlist ID
 * @param {string} value 
 * @returns {boolean}
 */
export function isValidPlaylistId(value) {
  return /^PL[a-zA-Z0-9_-]+$/.test(value);
}
